import { useShortcutDispatcher, useShortcutHandler } from '@core/shortcuts'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  isFsAccessSupported,
  pickIfcFile,
  pickIfcFiles,
} from '@core/session/ifcHandles'

const COLLAPSE_STORAGE_KEY = 'vn2r.ribbon.collapsed'

export function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function persistCollapsed(v: boolean) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(v))
  } catch {
    /* ignore */
  }
}

// App shells call this once to bootstrap the global keyboard
// dispatcher and register the ribbon-level handlers (toggling the
// ribbon itself can be rebound by the user just like any other
// action). File and section shortcuts also go here so they fire
// regardless of which ribbon tab is currently visible: the actual
// tab body unmounts when the user switches tabs.
export function useRibbonShortcuts() {
  useShortcutDispatcher()
  const alternarSectionBox = useAppStore((s) => s.alternarSectionBox)

  useShortcutHandler('ribbon.toggleCollapse', () => {
    const cur = loadCollapsed()
    persistCollapsed(!cur)
    window.dispatchEvent(new Event('vn2r:ribbon-collapsed-toggle'))
  })

  // O mesmo que o botão da Vista: reabre o painel se estiver fechado.
  useShortcutHandler('viewer.toggleSection', alternarSectionBox, [
    alternarSectionBox,
  ])

  // Esconde ou mostra o desenho da caixa (arestas, faces, pegas) sem
  // mexer no corte. Com a caixa desligada devolve false e a tecla segue.
  const alternarMostrarCaixa = useAppStore((s) => s.alternarMostrarCaixa)
  useShortcutHandler('viewer.toggleSectionBoxCaixa', alternarMostrarCaixa, [
    alternarMostrarCaixa,
  ])

  useShortcutHandler('file.openIfc', () => {
    if (!viewerApi.hasIfcLoader()) return
    void (async () => {
      if (isFsAccessSupported()) {
        const picked = await pickIfcFile()
        if (!picked) return
        await viewerApi.loadIfcFile(picked.file, {
          replace: true,
          handle: picked.handle,
        })
      }
    })()
  })

  useShortcutHandler('file.addIfc', () => {
    if (!viewerApi.hasIfcLoader()) return
    void (async () => {
      if (isFsAccessSupported()) {
        const picked = await pickIfcFiles()
        if (picked.length === 0) return
        for (const item of picked) {
          await viewerApi.loadIfcFile(item.file, {
            replace: false,
            handle: item.handle,
          })
        }
      }
    })()
  })
}
